import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn, getRegionActionStatus } from '@/lib/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const useTable = () => {
  const { t } = useTranslation();

  const formatPersonName = (p: {
    first_name: string;
    last_name: string;
    middle_name?: string;
  }) =>
    `${p?.last_name} ${p?.first_name}${p?.middle_name ? ` (${p?.middle_name})` : ''}`;

  const columns = useMemo(() => {
    return [
      {
        accessorKey: 'index',
        header: () => '№',
        cell: ({ row }: any) => (
          <div className="relative pl-3">
            <span
              className={cn(
                'w-[6px] absolute left-0 top-0 bottom-0',
                getRegionActionStatus(row.original?.status)?.color_bg ||
                  'bg-gray-graphite/60',
              )}
            ></span>
            <p>{row.index + 1}</p>
          </div>
        ),
      },
      {
        accessorKey: 'action_name',
        header: () => t('event_name'),
      },
      {
        accessorKey: 'farmer_area',
        header: () => t('farmer_entered_area'),
        cell: () => <p className="text-center">-</p>,
      },
      {
        accessorKey: 'total_inspected_area',
        header: () => t('checked_area'),
      },
      {
        accessorKey: 'responsible_people',
        header: () => t('stewardship'),
        cell: ({ row }: any) => {
          const people = row.original?.responsible_people ?? [];

          // Kamida bitta odamda first_name yoki last_name bo'lsa
          const hasValidPeople = people.some(
            (p: any) => p?.first_name || p?.last_name,
          );

          if (!hasValidPeople) {
            return (
              <span className="text-muted-foreground pointer-events-none select-none">
                —
              </span>
            );
          }

          const visible = people.slice(0, 2);
          const hiddenCount = people.length - visible.length;

          return (
            <div
              className="flex gap-1 flex-wrap"
              onClick={(e) => {
                if (!hasValidPeople) return;
                e.stopPropagation();
              }}
            >
              {visible.map((person: any, index: number) => (
                <Badge key={index} variant="secondary">
                  {person?.last_name ?? ''} {person?.first_name ?? ''}
                </Badge>
              ))}

              {hiddenCount > 0 && (
                <Popover modal={false}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => {
                        if (!hasValidPeople) return;
                        e.stopPropagation();
                      }}
                    >
                      <Badge variant="outline" className="cursor-pointer">
                        +{hiddenCount}
                      </Badge>
                    </button>
                  </PopoverTrigger>

                  <PopoverContent
                    side="right"
                    align="start"
                    className="z-[9999] w-80 max-h-[90vh] overflow-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-col gap-2">
                      {people.map((person: any, index: number) => (
                        <div
                          key={index}
                          className="flex flex-col border-b pb-1 last:border-0"
                        >
                          <span className="font-medium text-xs inherit">
                            {formatPersonName(person)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {person?.organization}
                          </span>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          );
        },
      },

      {
        accessorKey: 'status',
        header: () => t('event_status'),
        cell: ({ row }: any) => (
          <Badge
            className={cn(
              'rounded-xl ring-0 border-0 w-auto h-auto whitespace-nowrap px-2 py-0.5',
              getRegionActionStatus(row?.original?.status)?.color,
              getRegionActionStatus(row?.original?.status)?.color_bg,
            )}
          >
            {t(getRegionActionStatus(row?.original?.status)?.label as string)}
          </Badge>
        ),
      },
    ];
  }, [t]);

  return { columns };
};
